import React, { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import BottomSheet from "./BottomSheet";
import css from "./cpSourcingMobile.module.css";

const controlSx = { "& .MuiSvgIcon-root": { fontSize: 22 } };
const labelSx = {
  margin: 0,
  minHeight: 44,
  width: "100%",
  "& .MuiFormControlLabel-label": {
    fontFamily: "Inter-Medium",
    fontSize: 15,
    color: "#101828",
  },
};

const optionSearchSx = {
  mb: "8px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    minHeight: 44,
    fontFamily: "Inter-Medium",
    "& fieldset": { borderColor: "#d0d5dd" },
  },
  "& input": { fontSize: "16px", lineHeight: "22px" },
};

// Filter bottom sheet driven by the same `categories` shape LeadsFilterPanel
// takes, so each call site hands over the config object it already builds for
// desktop:
//   { label, options: [{id,label}], selectedIds, onToggle, emptyMessage,
//     searchable, searchPlaceholder }
//
// Staging matches desktop exactly: onToggle mutates the caller's *pending*
// selection and nothing filters until Apply. Dismissing the sheet therefore
// keeps pending ticks, as LeadsFilterPanel documents.
//
// `footerExtras` mirrors the prop of the same name on LeadsFilterPanel: optional
// nodes for filters that are not checkboxes, rendered below the categories.
// Defaults to nothing, so existing call sites are unaffected.
export default function MobileFilterSheet({
  open,
  title = "Filters",
  categories = [],
  onApply,
  onClearAll,
  onClose,
  footerExtras = null,
}) {
  // Per-category "filter the options" text, keyed by category label. Local to
  // the sheet — it narrows what is listed, never what is selected.
  const [optionSearch, setOptionSearch] = useState({});

  useEffect(() => {
    if (open) setOptionSearch({});
  }, [open]);

  const visibleOptions = (category) => {
    const term = (optionSearch[category.label] || "").trim().toLowerCase();
    if (!category.searchable || !term) return category.options || [];
    return (category.options || []).filter((o) =>
      String(o.label ?? "").toLowerCase().includes(term)
    );
  };

  return (
    <BottomSheet
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className={`${css.btn} ${css.btnSecondary}`}
            onClick={() => {
              onClearAll();
              onClose();
            }}
          >
            Clear all
          </button>
          <button
            type="button"
            className={`${css.btn} ${css.btnPrimary}`}
            onClick={() => {
              onApply();
              onClose();
            }}
          >
            Apply filters
          </button>
        </>
      }
    >
      {categories.map((category) => {
        const options = visibleOptions(category);
        return (
          <div key={category.label}>
            <div className={css.sheetGroupTitle}>{category.label}</div>

            {category.searchable && (category.options || []).length > 0 && (
              <TextField
                fullWidth
                size="small"
                placeholder={category.searchPlaceholder || "Search"}
                value={optionSearch[category.label] || ""}
                onChange={(e) =>
                  setOptionSearch((prev) => ({
                    ...prev,
                    [category.label]: e.target.value,
                  }))
                }
                sx={optionSearchSx}
              />
            )}

            {options.length === 0 ? (
              <div className={css.sheetEmpty}>
                {category.emptyMessage || "No Options Found"}
              </div>
            ) : (
              options.map((option) => (
                <FormControlLabel
                  key={option.id}
                  sx={labelSx}
                  control={
                    <Checkbox
                      sx={controlSx}
                      checked={(category.selectedIds || []).includes(option.id)}
                      onChange={() => category.onToggle(option.id)}
                    />
                  }
                  label={option.label}
                />
              ))
            )}
          </div>
        );
      })}
      {footerExtras}
    </BottomSheet>
  );
}
